import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import {
  ArrowRight, BookOpen, Copy, Database, KeyRound, Keyboard, LogOut, Monitor, Moon, PlayCircle, Sparkles, Sun,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/theme-context";
import { searchableNav } from "./navigation";
import { OPEN_COMMAND_PALETTE_EVENT } from "./command-palette-events";

const API_BASE = "https://api.genaicore.com/v1";

const SHORTCUTS: [string, string][] = [
  ["⌘ K / Ctrl K", "Open the command palette"],
  ["/", "Focus quick search"],
  ["?", "Show keyboard shortcuts"],
  ["Esc", "Close dialogs and menus"],
];

const itemClass =
  "flex items-center gap-3 h-11 px-3 rounded-xl text-sm cursor-pointer select-none aria-selected:bg-surface-muted data-[disabled=true]:opacity-50";

const isTyping = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable=true]");

/**
 * Global command palette (⌘K): fuzzy navigation to every page plus quick
 * actions. "?" opens the keyboard shortcut sheet.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"commands" | "shortcuts">("commands");
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setTheme } = useTheme();
  const isAdmin = user?.role === "admin";
  const pages = useMemo(() => searchableNav(isAdmin), [isAdmin]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setView("commands");
        setOpen((o) => !o);
      } else if (e.key === "?" && !isTyping(e.target)) {
        e.preventDefault();
        setView("shortcuts");
        setOpen(true);
      }
    };
    const onOpen = () => {
      setView("commands");
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpen);
    };
  }, []);

  const run = useCallback((fn: () => void) => {
    setOpen(false);
    // let the dialog close before navigating/acting so focus restores cleanly
    window.setTimeout(fn, 0);
  }, []);

  const copyBaseUrl = () =>
    navigator.clipboard
      .writeText(API_BASE)
      .then(() => toast.success("API base URL copied", { description: API_BASE }))
      .catch(() => toast.error("Couldn't access the clipboard"));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 gap-0 max-w-[620px] w-[calc(100vw-24px)] overflow-hidden top-[12vh] translate-y-0 data-[state=open]:slide-in-from-top-4">
        <DialogTitle className="sr-only">{view === "commands" ? "Command palette" : "Keyboard shortcuts"}</DialogTitle>
        <DialogDescription className="sr-only">Search pages and run quick actions.</DialogDescription>
        {view === "shortcuts" ? (
          <div className="p-6">
            <p className="text-lg font-medium">Keyboard shortcuts</p>
            <ul className="mt-4 divide-y divide-border/60">
              {SHORTCUTS.map(([keys, label]) => (
                <li key={keys} className="flex items-center justify-between py-3 text-sm">
                  <span>{label}</span>
                  <kbd className="rounded-md border border-border bg-surface-muted px-2 py-0.5 text-xs font-fragment">{keys}</kbd>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => setView("commands")} className="mt-4 text-sm text-muted-foreground hover:text-foreground">
              ← Back to commands
            </button>
          </div>
        ) : (
          <Command label="Command palette" className="flex flex-col max-h-[min(70vh,560px)]" loop>
            <div className="flex items-center gap-3 border-b border-border/70 px-4">
              <Sparkles className="h-4 w-4 text-brand-strong" />
              <Command.Input
                autoFocus
                placeholder="Search pages and actions…"
                className="h-14 flex-1 bg-transparent text-[15px] outline-hidden placeholder:text-subtle"
              />
              <kbd className="hidden sm:inline rounded-md border border-border px-1.5 text-[11px] text-muted-foreground">Esc</kbd>
            </div>
            <Command.List className="overflow-y-auto p-2">
              <Command.Empty className="py-10 text-center text-sm text-muted-foreground">No matches.</Command.Empty>

              <Command.Group heading="Quick actions" className="**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:py-2 **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:text-muted-foreground">
                <Command.Item className={itemClass} onSelect={() => run(() => navigate("/playground"))} keywords={["chat", "prompt", "test"]}>
                  <PlayCircle className="h-4 w-4" /> Open playground
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(() => navigate("/api-keys"))} keywords={["create", "token", "secret"]}>
                  <KeyRound className="h-4 w-4" /> Create an API key
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(() => navigate("/rag"))} keywords={["knowledge", "documents", "rag"]}>
                  <Database className="h-4 w-4" /> New knowledge base
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(() => navigate("/agents"))} keywords={["agent", "react", "tools"]}>
                  <Sparkles className="h-4 w-4" /> New agent
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(copyBaseUrl)} keywords={["endpoint", "url", "sdk", "openai"]}>
                  <Copy className="h-4 w-4" /> Copy API base URL
                  <span className="ml-auto text-xs text-subtle font-fragment truncate">{API_BASE}</span>
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(() => navigate("/docs"))} keywords={["help", "guide", "reference"]}>
                  <BookOpen className="h-4 w-4" /> Read the docs
                </Command.Item>
              </Command.Group>

              <Command.Group heading="Go to" className="**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:py-2 **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:text-muted-foreground">
                {pages.map((p) => (
                  <Command.Item
                    key={p.href}
                    value={`${p.name} ${p.href}`}
                    keywords={p.keywords ? p.keywords.split(/\s+/) : undefined}
                    className={itemClass}
                    onSelect={() => run(() => navigate(p.href))}
                  >
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    {p.name}
                    <span className="ml-auto text-xs text-subtle">{p.href}</span>
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group heading="Preferences" className="**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:py-2 **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:text-muted-foreground">
                <Command.Item className={itemClass} onSelect={() => run(() => setTheme("light"))} keywords={["theme", "appearance"]}>
                  <Sun className="h-4 w-4" /> Light theme
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(() => setTheme("dark"))} keywords={["theme", "appearance"]}>
                  <Moon className="h-4 w-4" /> Dark theme
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(() => setTheme("system"))} keywords={["theme", "appearance", "auto"]}>
                  <Monitor className="h-4 w-4" /> System theme
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => setView("shortcuts")} keywords={["keys", "hotkeys", "help"]}>
                  <Keyboard className="h-4 w-4" /> Keyboard shortcuts
                </Command.Item>
                <Command.Item className={itemClass} onSelect={() => run(logout)} keywords={["logout", "exit"]}>
                  <LogOut className="h-4 w-4" /> Sign out
                </Command.Item>
              </Command.Group>
            </Command.List>
          </Command>
        )}
      </DialogContent>
    </Dialog>
  );
}
